'use client';

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useIsWindowFocused } from '@/hooks/operation/common/useIsWindowFocused';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { type UserLendPositionsOrderBy, type UserLendPositionsOrderDirection, userLendPositionsQueryOptions } from './userLendPositionsQuery';

/**
 * Default sort applied by the Portfolio lend-positions table. Lives in this
 * feature-neutral data module (not in the DashboardPage config) so the nav
 * badge can request the same sort and share the table's React Query cache
 * without reaching into a page feature's internals.
 */
export const LEND_POSITIONS_DEFAULT_ORDER_BY: UserLendPositionsOrderBy = 'supplyAssetsUsd';
interface UseLendPositionsTableDataParams {
  accountAddress?: string;
  orderBy?: UserLendPositionsOrderBy;
  orderDirection?: UserLendPositionsOrderDirection;
  /**
   * Poll interval (ms) while the window is focused. Pass `false` to opt out of
   * polling entirely — e.g. the nav badge only warms/shares this query's cache
   * and doesn't need 10s freshness, so it avoids driving an app-wide refetch
   * storm. Because this is an observer-level option (not part of the query
   * key), opting out keeps the cache shared with the Portfolio table, which
   * reinstates the 10s poll via its own observer when opened. Defaults to 10s.
   */
  refetchInterval?: number | false;
}
export const useLendPositionsTableData = t0 => {
  const $ = _c(23);
  const {
    accountAddress,
    orderBy,
    orderDirection,
    refetchInterval: t1
  } = t0;
  const refetchInterval = t1 === undefined ? 10000 : t1;
  const isWindowFocused = useIsWindowFocused();
  const {
    marketLendPosition: isMarketLendPositionEnabled
  } = useFeatureFlags();
  let t2;
  if ($[0] !== accountAddress || $[1] !== isMarketLendPositionEnabled || $[2] !== orderBy || $[3] !== orderDirection) {
    t2 = userLendPositionsQueryOptions({
      userAddress: accountAddress,
      orderBy,
      orderDirection,
      enabled: isMarketLendPositionEnabled
    });
    $[0] = accountAddress;
    $[1] = isMarketLendPositionEnabled;
    $[2] = orderBy;
    $[3] = orderDirection;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isWindowFocused || $[6] !== refetchInterval) {
    t3 = query => isWindowFocused && (query.state.data?.items.length ?? 0) > 0 ? refetchInterval : false;
    $[5] = isWindowFocused;
    $[6] = refetchInterval;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = {
      ...t2,
      refetchInterval: t3,
      placeholderData: keepPreviousData
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    data,
    isLoading,
    isPlaceholderData,
    error
  } = useQuery(t4);
  let t5;
  if ($[11] !== data?.items || $[12] !== isMarketLendPositionEnabled) {
    t5 = isMarketLendPositionEnabled ? data?.items ?? [] : [];
    $[11] = data?.items;
    $[12] = isMarketLendPositionEnabled;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const items = t5;
  const partial = isMarketLendPositionEnabled && (data?.partial ?? false);
  const t6 = isMarketLendPositionEnabled ? data?.facets : undefined;
  const t7 = isMarketLendPositionEnabled ? data?.countTotal ?? 0 : 0;
  const t8 = isMarketLendPositionEnabled && isLoading;
  const t9 = isMarketLendPositionEnabled && isPlaceholderData;
  const t10 = isMarketLendPositionEnabled ? error ?? undefined : undefined;
  const t11 = !isLoading && !error && !partial && items.length === 0;
  let t12;
  if ($[14] !== items || $[15] !== partial || $[16] !== t10 || $[17] !== t11 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t12 = {
      lendPositions: items,
      facets: t6,
      partial,
      countTotal: t7,
      loading: t8,
      isPlaceholderData: t9,
      error: t10,
      isNotFound: t11
    };
    $[14] = items;
    $[15] = partial;
    $[16] = t10;
    $[17] = t11;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
};